import React, { useCallback, useEffect, useRef, useState } from "react";
import { APP_CONFIG } from "../../config/appConfig";
import { TEXTS } from "../../config/texts";
import "./PinTerminal.css";

export default function PinTerminal({ active, onSuccess, audio, vibrate }) {
    const [entered, setEntered] = useState("");
    const [status, setStatus] = useState("idle");
    const [message, setMessage] = useState(TEXTS.enterCode);
    const timer = useRef(null);
    const submitted = useRef(false);

    useEffect(() => () => clearTimeout(timer.current), []);

    const reset = useCallback(() => {
        submitted.current = false;
        setEntered("");
        setStatus("idle");
        setMessage(TEXTS.enterCode);
    }, []);

    const submit = useCallback(
        (value) => {
            if (!active || submitted.current || !value) return;
            submitted.current = true;
            if (value === APP_CONFIG.pin) {
                setStatus("granted");
                setMessage(TEXTS.accessGranted);
                audio.playGranted();
                vibrate([35, 45, 35, 45, 120]);
                timer.current = setTimeout(onSuccess, 850);
                return;
            }
            setStatus("denied");
            setMessage(TEXTS.accessDenied);
            audio.playError();
            vibrate([70, 35, 70]);
            timer.current = setTimeout(reset, 720);
        },
        [active, audio, onSuccess, reset, vibrate]
    );

    const addDigit = useCallback(
        (digit) => {
            if (!active || submitted.current) return;
            audio.playClick();
            vibrate(8);
            setEntered((previous) => {
                const next = `${previous}${digit}`.slice(0, APP_CONFIG.pin.length);
                if (next.length === APP_CONFIG.pin.length) {
                    timer.current = setTimeout(() => submit(next), 130);
                }
                return next;
            });
        },
        [active, audio, submit, vibrate]
    );

    const backspace = useCallback(() => {
        if (!active || submitted.current) return;
        audio.playBackspace();
        vibrate(6);
        setEntered((previous) => previous.slice(0, -1));
    }, [active, audio, vibrate]);

    useEffect(() => {
        const keydown = (event) => {
            if (!active) return;
            if (/^[0-9]$/.test(event.key)) addDigit(event.key);
            if (event.key === "Backspace") backspace();
            if (event.key === "Enter") submit(entered);
        };
        window.addEventListener("keydown", keydown);
        return () => window.removeEventListener("keydown", keydown);
    }, [active, addDigit, backspace, entered, submit]);

    return (
        <div className={`pin-terminal pin-terminal--${status} ${active ? "" : "pin-terminal--leaving"}`} aria-label="Factory access terminal">
            <div className="pin-terminal__mount" />
            <div className="pin-terminal__body">
                <div className="pin-terminal__topline">
                    <span>HC-01</span>
                    <span className="pin-terminal__led" />
                </div>
                <div className="pin-terminal__screen">
                    <div className="pin-terminal__screen-title">{TEXTS.systemOffline}</div>
                    <div className="pin-terminal__screen-subtitle">{TEXTS.authRequired}</div>
                    <div className="pin-terminal__code" aria-label={`${entered.length} digits entered`}>
                        {Array.from({ length: APP_CONFIG.pin.length }, (_, index) => (
                            <span key={index} className={index < entered.length ? "is-filled" : ""} />
                        ))}
                    </div>
                    <div className="pin-terminal__message">{message}</div>
                </div>
                <div className="pin-terminal__keypad">
                    {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
                        <button key={digit} type="button" onPointerDown={() => addDigit(digit)} aria-label={`Digit ${digit}`}>
                            {digit}
                        </button>
                    ))}
                    <button type="button" className="is-function" onPointerDown={backspace} aria-label="Delete digit">DEL</button>
                    <button type="button" onPointerDown={() => addDigit("0")} aria-label="Digit 0">0</button>
                    <button type="button" className="is-confirm" onPointerDown={() => submit(entered)} aria-label="Confirm code">OK</button>
                </div>
            </div>
        </div>
    );
}
